import {
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
} from "@/components/ui/accordion";
import { FaCircleMinus, FaCirclePlus } from "react-icons/fa6";
import Link from "next/link";

import FancyButton from "../ui/fancy-button";
import BasicBlock from "./basic-block";
import { useStrings } from "@/lib/i18n/context";

export const FAQ_DATA = [
  {
    question: "Can I cancel my plan?",
    answer:
      "Yes. You can cancel your plan anytime via settings. Your plan will remain active until the end of the billing cycle.",
  },
  {
    question: "What is a workflow credit?",
    answer:
      "A workflow credit is one video generated by Crayo's AI. This includes AutoClip, Reddit videos, Fake Texts and more.",
  },
  {
    question: "How do I view my usage?",
    answer:
      "You can view your usage in settings. Click your profile picture in the top right corner.",
  },
  {
    question: "Do you have a refund policy?",
    answer:
      "Unfortunately, we do not offer refunds. You can cancel your plan anytime and your plan will remain active until the end of the billing cycle.",
  },
  {
    question: "What is an export minute?",
    answer:
      "An export minute is the length of video you can generate with Crayo. For example, if you have 60 export minutes, you can generate a 1 minute video 60 times.",
  },
  {
    question: "Can I monetize videos created with Crayo?",
    answer: "Yes. You fully own the rights to the videos you create with Crayo.",
  },
  {
    question: "Can I generate in other languages?",
    answer:
      "Yes. We support most languages. Simply provide the text in the language you want to generate in. Contact support if you need a language that is not supported.",
  },
  {
    question: "Do unused credits roll over?",
    answer:
      "Subscription credits refill every month on every plan term, whether you pay monthly, quarterly or yearly, and unused credits do not roll over. Credits from one-time top-ups are separate from your monthly allowance.",
  },
];

export type FaqItem = {
  question: string;
  answer: string;
  link?: { href: string; label: string };
};

const FAQHeader = () => {
  const s = useStrings();
  return (
    <h3 className="text-center text-[35px] font-semibold md:text-[45px]">
      <p className="text-[30px] font-medium text-[#335CFF]">{s.faq.eyebrow}</p>
      <span className="hidden select-none sm:block">{s.faq.heading}</span>
      <span className="block select-none md:hidden">{s.faq.headingMobile}</span>
    </h3>
  );
};

const FAQ = ({ items = FAQ_DATA }: { items?: FaqItem[] }) => {
  const s = useStrings();
  return (
    <BasicBlock
      header={<FAQHeader />}
      contentStyle="flex-col md:gap-16 gap-6 items-center justify-center"
    >
      <Accordion
        type="single"
        collapsible
        className="grid w-full grid-cols-1 gap-[22px] md:grid-cols-2"
      >
        {items.map((item, index: number) => (
          <AccordionItem
            key={index}
            value={`item-${index}`}
            className={
              "max-h-fit rounded-2xl border border-[#EFF2FF] bg-white p-[30px] py-2 shadow-sm"
            }
          >
            <AccordionTrigger className="text-left text-[12px] font-semibold tracking-wide text-[#101828] md:text-base">
              {item.question}
              <FaCirclePlus className="mb-1 mr-2 size-5 shrink-0 text-[#525866] transition-transform duration-200 group-data-[state=open]:hidden" />
              <FaCircleMinus className="mb-1 mr-2 size-5 shrink-0 text-[#525866] transition-transform duration-200 group-data-[state=closed]:hidden" />
            </AccordionTrigger>
            <AccordionContent
              forceMount
              className="text-sm font-light leading-normal text-[#525866] [[data-state=closed]>&]:hidden"
            >
              {item.answer}
              {item.link && (
                <Link
                  href={item.link.href}
                  prefetch={false}
                  className="mt-2 block w-fit font-medium text-crayoBlue underline decoration-crayoBlue/30 underline-offset-[3px] hover:decoration-crayoBlue"
                >
                  {item.link.label}
                </Link>
              )}
            </AccordionContent>
          </AccordionItem>
        ))}
      </Accordion>
      <div className="flex w-full flex-col items-start justify-between gap-5 rounded-2xl border border-[#CACFD8] bg-[#F9FBFF] px-4 py-6 sm:px-[40px] sm:py-[30px] md:flex-row md:items-center md:gap-0">
        <div className="flex flex-col gap-1">
          <h3 className="text-xl font-medium text-[#101828]">{s.faq.stillTitle}</h3>
          <p className="text-sm tracking-wide text-[#525866]">{s.faq.stillBody}</p>
        </div>
        <FancyButton className="whitespace-nowrap" href="/discord" size="lg" inNewTab>
          {s.faq.contact}
        </FancyButton>
      </div>
    </BasicBlock>
  );
};

export default FAQ;
